import { useContext } from 'react';
import { Modal } from 'antd';

import { DocumentSummaryEntry } from '@totem/components/documents/types';
import { isMemberOfAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DOCUMENTS_ENDPOINT } from '@totem/utilities/endpoints';

// Shared by the document tables: whether the user may open a document, and opening it (download, or a
// confirmed jump to an external web link).
const useDocumentAccess = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const canOpen = (record: DocumentSummaryEntry) =>
    record.document.securityGroups.length === 0 ||
    isMemberOfAny(userProfile, record.document.securityGroups, {
      regionId: record.region.id,
      buildingId: record.building.id,
      controlSystemId: record.controlSystem.id,
    });

  const download = async (record: DocumentSummaryEntry) => {
    try {
      const response = await fetch(
        `${DOCUMENTS_ENDPOINT}/${record.document.id}/${record.document.version}`,
        {
          method: 'GET',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      );
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;

      anchor.download = record.document.filename;

      document.body.appendChild(anchor);

      anchor.click();

      window.URL.revokeObjectURL(url);

      document.body.removeChild(anchor);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download error:', error);
    }
  };

  const openLink = (record: DocumentSummaryEntry) =>
    Modal.confirm({
      title: 'External Link',
      content: `You are about to navigate to an external URL: ${record.document.storagePath}`,
      okText: 'Continue',
      cancelText: 'Cancel',
      onOk: () =>
        window.open(
          record.document.storagePath,
          record.document.displayName,
          'noopener,noreferrer',
        ),
    });

  const open = (record: DocumentSummaryEntry) =>
    record.document.fileType === 'Link' ? openLink(record) : download(record);

  return { canOpen, open };
};

export default useDocumentAccess;
